import { apiClient } from '@/shared/api/client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';

import type {
  PumsemDiff,
  PumsemEdition,
  PumsemItem,
  PumsemSearchParams,
  PumsemSearchResult,
  PumsemTree,
} from '../model/pumsem.type';

/**
 * 품셈해설 조회 호출부.
 *
 * 전부 `skipAuthRedirect` 를 켠다 — 열람은 **비로그인이 기본**이다. 검색하다가
 * 로그인 화면으로 튕기면 국내 대체재(회원제 e-book)와 같은 진입장벽이 되고,
 * 색인도 그 순간 죽는다. 로그인이 필요한 지점은 내보내기·담기이지 열람이 아니다.
 */
export const pumsemApi = {
  getEditions: async (): Promise<PumsemEdition[]> => {
    const { data } = await apiClient.get<PumsemEdition[]>(
      API_ENDPOINTS.pumsem.editions,
      { skipAuthRedirect: true },
    );
    return data;
  },

  /**
   * 부문 → 장 → 절 → 항 트리. 본문은 실려 오지 않는다.
   *
   * 검색만 있으면 **무엇이 들어 있는지 모르는 사람은 아무것도 못 본다** —
   * 판을 새로 등록해 놓고 `/pumsem` 을 열면 빈 화면이었다. 훑기와 찾기는
   * 서로를 대체하지 못한다.
   */
  getTree: async (edition?: string): Promise<PumsemTree> => {
    const { data } = await apiClient.get<PumsemTree>(
      API_ENDPOINTS.pumsem.tree,
      { params: { edition }, skipAuthRedirect: true },
    );
    return data;
  },

  search: async (params: PumsemSearchParams): Promise<PumsemSearchResult> => {
    const { data } = await apiClient.get<PumsemSearchResult>(
      API_ENDPOINTS.pumsem.search,
      { params, skipAuthRedirect: true },
    );
    return data;
  },

  /** `edition` 생략 = 최신 발행판. 판 고정 링크는 반드시 값을 넘긴다. */
  getItem: async (
    division: string,
    code: string,
    edition?: string,
  ): Promise<PumsemItem> => {
    const { data } = await apiClient.get<PumsemItem>(
      API_ENDPOINTS.pumsem.item,
      { params: { division, code, edition }, skipAuthRedirect: true },
    );
    return data;
  },

  getDiff: async (
    division: string,
    code: string,
    edition?: string,
  ): Promise<PumsemDiff> => {
    const { data } = await apiClient.get<PumsemDiff>(
      API_ENDPOINTS.pumsem.diff,
      { params: { division, code, edition }, skipAuthRedirect: true },
    );
    return data;
  },
};
